import React, { useState, useEffect } from 'react';
import ReactGA from 'react-ga4';
import Clarity from '@microsoft/clarity';

const PersonalDashboard = ({ user }) => {
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [stats, setStats] = useState({
    totalAnalyses: 0,
    avgScore: 0,
    topCategory: '',
    recentActivity: []
  });
  const [timeFilter, setTimeFilter] = useState('week'); // week, month, all
  const [sortBy, setSortBy] = useState('date'); // date, score, title
  const [favorites, setFavorites] = useState([]);

  useEffect(() => {
    loadData();
    
    // Track dashboard view
    ReactGA.event({
      category: 'Dashboard',
      action: 'Dashboard Viewed',
      label: 'Personal Analytics'
    });
    Clarity.event('dashboard_viewed');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, timeFilter]);

  const loadData = () => {
    if (!user) return;

    // Load analysis history from localStorage
    const userKey = user.id || user.email;
    const historyKey = `analysis_history_${userKey}`;
    const savedHistory = localStorage.getItem(historyKey);
    const history = savedHistory ? JSON.parse(savedHistory) : [];

    // Load favorites
    const favKey = `favorites_${userKey}`;
    const savedFavorites = localStorage.getItem(favKey);
    const favs = savedFavorites ? JSON.parse(savedFavorites) : [];
    setFavorites(favs);

    // Filter by time
    const filtered = filterByTime(history, timeFilter);
    setAnalysisHistory(filtered);

    // Calculate stats
    calculateStats(filtered);
  };

  const filterByTime = (history, filter) => {
    const now = new Date();
    let cutoffDate = new Date();

    if (filter === 'week') {
      cutoffDate.setDate(now.getDate() - 7);
    } else if (filter === 'month') {
      cutoffDate.setMonth(now.getMonth() - 1);
    } else {
      return history; // all
    }

    return history.filter(item => new Date(item.timestamp) >= cutoffDate);
  };

  const calculateStats = (history) => {
    if (history.length === 0) {
      setStats({
        totalAnalyses: 0,
        avgScore: 0,
        topCategory: 'N/A',
        recentActivity: []
      });
      return;
    }

    const totalAnalyses = history.length;
    const avgScore = history.reduce((sum, item) => sum + (item.score || 0), 0) / totalAnalyses;

    // Find top category
    const categoryScores = {};
    history.forEach(item => {
      if (item.ratings) {
        Object.entries(item.ratings).forEach(([cat, rating]) => {
          if (!categoryScores[cat]) categoryScores[cat] = [];
          categoryScores[cat].push(rating.score || 0);
        });
      }
    });

    let topCategory = 'N/A';
    let maxAvg = 0;
    Object.entries(categoryScores).forEach(([cat, scores]) => {
      const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
      if (avg > maxAvg) {
        maxAvg = avg;
        topCategory = cat;
      }
    });

    setStats({
      totalAnalyses,
      avgScore: avgScore.toFixed(2),
      topCategory: topCategory.replace(/_/g, ' '),
      recentActivity: history.slice(0, 5)
    });
  };

  const sortHistory = (history) => {
    const sorted = [...history];
    if (sortBy === 'date') {
      sorted.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    } else if (sortBy === 'score') {
      sorted.sort((a, b) => (b.score || 0) - (a.score || 0));
    } else if (sortBy === 'title') {
      sorted.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    }
    return sorted;
  };

  const toggleFavorite = (videoId) => {
    const userKey = user.id || user.email;
    const favKey = `favorites_${userKey}`;
    
    let updatedFavorites;
    if (favorites.includes(videoId)) {
      updatedFavorites = favorites.filter(id => id !== videoId);
    } else {
      updatedFavorites = [...favorites, videoId];
    }
    
    setFavorites(updatedFavorites);
    localStorage.setItem(favKey, JSON.stringify(updatedFavorites));

    ReactGA.event({
      category: 'Dashboard',
      action: favorites.includes(videoId) ? 'Remove Favorite' : 'Add Favorite',
      label: videoId
    });
  };

  const deleteFromHistory = (videoId) => {
    if (!window.confirm('Remove this analysis from your history?')) return;

    const userKey = user.id || user.email;
    const historyKey = `analysis_history_${userKey}`;
    const savedHistory = localStorage.getItem(historyKey);
    const history = savedHistory ? JSON.parse(savedHistory) : [];
    
    const updated = history.filter(item => item.video_id !== videoId);
    localStorage.setItem(historyKey, JSON.stringify(updated));
    loadData();

    ReactGA.event({
      category: 'Dashboard',
      action: 'Delete History Item',
      label: videoId
    });
  };

  const sortedHistory = sortHistory(analysisHistory);

  // Format duration helper
  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div style={{
      minHeight: '100vh',
      padding: '80px 20px 40px',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background Elements */}
      <div style={{
        position: 'absolute',
        top: '10%',
        left: '10%',
        width: '400px',
        height: '400px',
        background: 'radial-gradient(circle, rgba(139, 92, 246, 0.1), transparent)',
        borderRadius: '50%',
        filter: 'blur(80px)',
        pointerEvents: 'none',
        zIndex: 0
      }} />
      <div style={{
        position: 'absolute',
        bottom: '20%',
        right: '5%',
        width: '300px',
        height: '300px',
        background: 'radial-gradient(circle, rgba(236, 72, 153, 0.1), transparent)',
        borderRadius: '50%',
        filter: 'blur(60px)',
        pointerEvents: 'none',
        zIndex: 0
      }} />

      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        position: 'relative',
        zIndex: 1
      }}>
        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '3rem'
        }}>
          <h1 style={{
            fontSize: '3rem',
            fontWeight: 'bold',
            marginBottom: '0.5rem',
            background: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Personal Dashboard
          </h1>
          <p style={{
            fontSize: '1.1rem',
            color: '#d1d5db'
          }}>
            Your analysis history, stats, and insights
          </p>
        </div>

        {/* Stats Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '1.5rem',
          marginBottom: '3rem'
        }}>
          {/* Total Analyses */}
          <div style={{
            background: 'rgba(15, 15, 35, 0.6)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(139, 92, 246, 0.2)',
            borderRadius: '16px',
            padding: '2rem',
            transition: 'all 0.3s ease'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.5)';
            e.currentTarget.style.boxShadow = '0 10px 30px rgba(139, 92, 246, 0.3)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.2)';
            e.currentTarget.style.boxShadow = 'none';
          }}>
            <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#8b5cf6' }}>
              {stats.totalAnalyses}
            </div>
            <div style={{ fontSize: '1rem', color: '#d1d5db' }}>Total Analyses</div>
          </div>

          {/* Average Score */}
          <div style={{
            background: 'rgba(15, 15, 35, 0.6)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(236, 72, 153, 0.2)',
            borderRadius: '16px',
            padding: '2rem',
            transition: 'all 0.3s ease'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.borderColor = 'rgba(236, 72, 153, 0.5)';
            e.currentTarget.style.boxShadow = '0 10px 30px rgba(236, 72, 153, 0.3)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.borderColor = 'rgba(236, 72, 153, 0.2)';
            e.currentTarget.style.boxShadow = 'none';
          }}>
            <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#ec4899' }}>
              {stats.avgScore}
            </div>
            <div style={{ fontSize: '1rem', color: '#d1d5db' }}>Average Score</div>
          </div>

          {/* Top Category */}
          <div style={{
            background: 'rgba(15, 15, 35, 0.6)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(6, 182, 212, 0.2)',
            borderRadius: '16px',
            padding: '2rem',
            transition: 'all 0.3s ease'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.borderColor = 'rgba(6, 182, 212, 0.5)';
            e.currentTarget.style.boxShadow = '0 10px 30px rgba(6, 182, 212, 0.3)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.borderColor = 'rgba(6, 182, 212, 0.2)';
            e.currentTarget.style.boxShadow = 'none';
          }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#06b6d4', textTransform: 'capitalize' }}>
              {stats.topCategory}
            </div>
            <div style={{ fontSize: '1rem', color: '#d1d5db' }}>Top Category</div>
          </div>

          {/* Favorites Count */}
          <div style={{
            background: 'rgba(15, 15, 35, 0.6)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(251, 191, 36, 0.2)',
            borderRadius: '16px',
            padding: '2rem',
            transition: 'all 0.3s ease'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.borderColor = 'rgba(251, 191, 36, 0.5)';
            e.currentTarget.style.boxShadow = '0 10px 30px rgba(251, 191, 36, 0.3)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.borderColor = 'rgba(251, 191, 36, 0.2)';
            e.currentTarget.style.boxShadow = 'none';
          }}>
            <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#fbbf24' }}>
              {favorites.length}
            </div>
            <div style={{ fontSize: '1rem', color: '#d1d5db' }}>Favorites</div>
          </div>
        </div>

        {/* Filters and Sort */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          {/* Time Filter */}
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {['week', 'month', 'all'].map((filter) => (
              <button
                key={filter}
                onClick={() => setTimeFilter(filter)}
                style={{
                  padding: '0.75rem 1.5rem',
                  borderRadius: '12px',
                  border: timeFilter === filter 
                    ? '2px solid rgba(139, 92, 246, 0.5)' 
                    : '1px solid rgba(139, 92, 246, 0.2)',
                  background: timeFilter === filter
                    ? 'rgba(139, 92, 246, 0.2)'
                    : 'rgba(15, 15, 35, 0.6)',
                  color: timeFilter === filter ? '#8b5cf6' : '#d1d5db',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: timeFilter === filter ? 'bold' : 'normal',
                  textTransform: 'capitalize',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  if (timeFilter !== filter) {
                    e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.4)';
                  }
                }}
                onMouseOut={(e) => {
                  if (timeFilter !== filter) {
                    e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.2)';
                  }
                }}
              >
                {filter === 'week' ? 'Last Week' : filter === 'month' ? 'Last Month' : 'All Time'}
              </button>
            ))}
          </div>

          {/* Sort By */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <span style={{ color: '#d1d5db', fontSize: '1rem' }}>Sort by:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                padding: '0.75rem 1.5rem',
                borderRadius: '12px',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                background: 'rgba(15, 15, 35, 0.6)',
                color: '#d1d5db',
                fontSize: '1rem',
                cursor: 'pointer',
                outline: 'none'
              }}
            >
              <option value="date">Date</option>
              <option value="score">Score</option>
              <option value="title">Title</option>
            </select>
          </div>
        </div>

        {/* Analysis History */}
        <div style={{
          background: 'rgba(15, 15, 35, 0.6)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(139, 92, 246, 0.2)',
          borderRadius: '16px',
          padding: '2rem',
          marginBottom: '2rem'
        }}>
          <h2 style={{
            fontSize: '1.8rem',
            fontWeight: 'bold',
            marginBottom: '1.5rem',
            color: '#e5e7eb'
          }}>
            Analysis History
          </h2>

          {sortedHistory.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '3rem',
              color: '#9ca3af'
            }}>
              <p style={{ fontSize: '1.2rem' }}>No analyses yet</p>
              <p style={{ fontSize: '1rem', marginTop: '0.5rem' }}>
                Start analyzing videos to see your history here!
              </p>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gap: '1rem'
            }}>
              {sortedHistory.map((item, idx) => (
                <div
                  key={idx}
                  style={{
                    background: 'rgba(55, 65, 81, 0.3)',
                    border: '1px solid rgba(139, 92, 246, 0.2)',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '1rem',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.5)';
                    e.currentTarget.style.transform = 'translateX(5px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.2)';
                    e.currentTarget.style.transform = 'translateX(0)';
                  }}
                >
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                    {/* Thumbnail */}
                    {item.thumbnail && (
                      <img
                        src={item.thumbnail}
                        alt={item.title || 'Video'}
                        style={{
                          width: '120px',
                          height: '90px',
                          objectFit: 'cover',
                          borderRadius: '12px',
                          border: '2px solid rgba(139, 92, 246, 0.3)'
                        }}
                      />
                    )}
                    
                    {/* Video Info */}
                    <div style={{ flex: 1 }}>
                      <h3 style={{
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        color: '#e5e7eb',
                        marginBottom: '0.75rem'
                      }}>
                        {item.title || 'Untitled Video'}
                      </h3>
                      
                      {/* Metadata stats */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: '0.5rem',
                        fontSize: '0.85rem',
                        color: '#d1d5db',
                        marginBottom: '0.5rem'
                      }}>
                        <span>{item.metadata?.views ? new Intl.NumberFormat().format(item.metadata.views) : 'N/A'} views</span>
                        <span>{item.metadata?.likes ? new Intl.NumberFormat().format(item.metadata.likes) : 'N/A'} likes</span>
                        <span>{item.metadata?.comments ? new Intl.NumberFormat().format(item.metadata.comments) : 'N/A'} comments</span>
                        <span>{formatDuration(item.metadata?.duration)}</span>
                      </div>
                      
                      {/* Additional info */}
                      <div style={{
                        fontSize: '0.9rem',
                        color: '#9ca3af',
                        display: 'flex',
                        gap: '1rem',
                        flexWrap: 'wrap'
                      }}>
                        <span>{new Date(item.timestamp).toLocaleDateString()}</span>
                        <span>Score: {item.score?.toFixed(1) || 'N/A'}/5.0</span>
                        {item.metadata?.channel && <span>{item.metadata.channel}</span>}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                    {/* Favorite Button */}
                    <button
                      onClick={() => toggleFavorite(item.video_id)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        fontSize: '1.5rem',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.2)'}
                      onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                      title={favorites.includes(item.video_id) ? 'Remove from favorites' : 'Add to favorites'}
                    >
                      {favorites.includes(item.video_id) ? '★' : '☆'}
                    </button>

                    {/* View Button */}
                    <button
                      onClick={() => window.open(`https://youtube.com/watch?v=${item.video_id}`, '_blank')}
                      style={{
                        padding: '0.5rem 1rem',
                        borderRadius: '8px',
                        border: '1px solid rgba(139, 92, 246, 0.3)',
                        background: 'rgba(139, 92, 246, 0.2)',
                        color: '#8b5cf6',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: 'bold',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.background = 'rgba(139, 92, 246, 0.3)';
                        e.currentTarget.style.transform = 'scale(1.05)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = 'rgba(139, 92, 246, 0.2)';
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    >
                      View
                    </button>

                    {/* Delete Button */}
                    <button
                      onClick={() => deleteFromHistory(item.video_id)}
                      style={{
                        padding: '0.5rem 1rem',
                        borderRadius: '8px',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        background: 'rgba(239, 68, 68, 0.2)',
                        color: '#ef4444',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: 'bold',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.background = 'rgba(239, 68, 68, 0.3)';
                        e.currentTarget.style.transform = 'scale(1.05)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PersonalDashboard;

